<script setup>
// M26.2 / M26.5 Part 7 — conceptual pipeline visualization for the AI
// Workforce Overview. Presentational only: the parent decides which nodes
// are "active" from already-fetched workforce data (registry status + live
// task counts). `projectUnits` is never hardcoded — it's the same real
// per-project units array the cards render, so the fan-out under the
// Project layer updates automatically as projects are added or removed.
import { ref } from 'vue';
import GeneratedAvatar from 'dashboard/components-next/avatar/GeneratedAvatar.vue';
import { departmentColor } from './departmentColors';

defineProps({
  nodes: {
    type: Array,
    required: true,
    // [{ key, label, icon, active, count }]
  },
  projectUnits: {
    type: Array,
    default: () => [],
    // [{ key, name }] — real Nexceo::AiEmployee-backed units
  },
});

const projectColor = departmentColor('project');

// M26.5.1 Part 6 — Project Layer fan-out is expanded by default (matches the
// M26.5 behavior) but can be collapsed; animated via a CSS grid-template-rows
// transition (0fr <-> 1fr) rather than a JS height measurement.
const isProjectLayerExpanded = ref(true);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="rounded-xl border border-n-weak bg-n-background p-4 overflow-x-auto"
  >
    <div class="flex items-center gap-1 min-w-max">
      <template v-for="(node, index) in nodes" :key="node.key">
        <div class="flex flex-col items-center gap-1.5 w-24 shrink-0">
          <div
            class="relative flex items-center justify-center w-11 h-11 rounded-2xl border transition-colors"
            :class="
              node.active
                ? 'bg-woot-50 border-woot-300 dark:bg-woot-500/10 dark:border-woot-500/40'
                : 'bg-n-alpha-1 border-n-weak'
            "
          >
            <span
              v-if="node.active"
              class="absolute inset-0 rounded-2xl bg-woot-400/20 animate-pulse"
            />
            <span
              class="relative size-[18px]"
              :class="[
                node.icon,
                node.active
                  ? 'text-woot-600 dark:text-woot-400'
                  : 'text-n-slate-7',
              ]"
            />
          </div>
          <p
            class="text-[11px] font-medium text-center leading-tight"
            :class="node.active ? 'text-n-slate-12' : 'text-n-slate-8'"
          >
            {{ node.label }}
          </p>
          <span
            v-if="node.count"
            class="text-[10px] px-1.5 rounded-full font-medium bg-n-alpha-2 text-n-slate-8 tabular-nums"
          >
            {{ node.count }}
          </span>
        </div>

        <div
          v-if="index < nodes.length - 1"
          class="h-px flex-1 min-w-6 relative overflow-hidden -mt-4"
          :class="
            node.active && nodes[index + 1]?.active
              ? 'bg-woot-300 dark:bg-woot-500/40'
              : 'bg-n-weak'
          "
        >
          <span
            v-if="node.active && nodes[index + 1]?.active"
            class="connector-flow absolute inset-y-0 w-4 bg-gradient-to-r from-transparent via-woot-400 to-transparent"
          />
        </div>
      </template>
    </div>

    <!-- Project AI layer — every real Nexceo::AiEmployee, never hardcoded -->
    <div v-if="projectUnits.length" class="flex flex-col items-center mt-2">
      <button
        class="flex flex-col items-center group"
        :aria-expanded="isProjectLayerExpanded"
        @click="isProjectLayerExpanded = !isProjectLayerExpanded"
      >
        <span
          class="i-lucide-chevron-down size-3.5 text-n-slate-6 transition-transform duration-200 group-hover:text-n-slate-8"
          :class="{ '-rotate-90': !isProjectLayerExpanded }"
        />
        <p
          class="text-[10px] font-semibold text-n-slate-6 uppercase tracking-wider mt-1 mb-2 group-hover:text-n-slate-8"
        >
          Project AI Layer · {{ projectUnits.length }}
        </p>
      </button>
      <div
        class="grid w-full transition-[grid-template-rows] duration-300 ease-out"
        :style="{ gridTemplateRows: isProjectLayerExpanded ? '1fr' : '0fr' }"
      >
        <div class="overflow-hidden">
          <div class="flex flex-wrap justify-center gap-3 max-w-2xl mx-auto">
            <div
              v-for="unit in projectUnits"
              :key="unit.key"
              class="flex flex-col items-center gap-1 w-16"
              :title="unit.name"
            >
              <GeneratedAvatar
                :name="unit.name"
                :color="projectColor.hex"
                variant="project"
                :seed="unit.key"
                :size="28"
              />
              <p
                class="text-[10px] text-n-slate-8 text-center leading-tight truncate w-full"
              >
                {{ unit.name }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.connector-flow {
  animation: connector-flow-move 1.8s linear infinite;
}
@keyframes connector-flow-move {
  0% {
    left: -10%;
  }
  100% {
    left: 100%;
  }
}
</style>
